import React, { useState, useEffect } from 'react';
import {
  FaBook, FaCode, FaCopy, FaCheck, FaExclamationTriangle,
  FaCheckCircle, FaTimesCircle, FaChevronRight, FaDatabase,
} from 'react-icons/fa';
import { documentationAPI } from '../../services/api';
import '../../styles/payment-api-pro.css';

const APIs = () => {
  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [selectedTab, setSelectedTab] = useState('request');

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await documentationAPI.getAPIDocumentation();
        setDocData(response.data.data);
      } catch (err) {
        console.error('Failed to fetch documentation:', err);
        setError('Failed to load API documentation');
      } finally {
        setLoading(false);
      }
    };

    fetchDocs();
  }, []);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const renderJSON = (data) => JSON.stringify(data, null, 2);

  if (loading) {
    return (
      <div className="payapi-docs payapi-loading-state">
        <div className="payapi-spinner"></div>
        <p>Loading API Documentation...</p>
      </div>
    );
  }

  if (error || !docData) {
    return (
      <div className="payapi-docs payapi-error-state">
        <FaExclamationTriangle />
        <p>{error || 'No documentation data available'}</p>
      </div>
    );
  }

  const payment = docData.payment_initiation;

  return (
    <div className="payapi-docs">
      {/* Header */}
      <header className="payapi-header">
        <div className="payapi-header-content">
          <h1>{docData.api_name}</h1>
          <p>v{docData.version} • {docData.environment} • A1XPAY</p>
        </div>
      </header>

      {/* Quick Info */}
      <div className="payapi-quickinfo">
        <div className="payapi-infoitem">
          <span className="payapi-label">Base URL</span>
          <code>{docData.base_url}</code>
        </div>
        <div className="payapi-infoitem">
          <span className="payapi-label">Endpoint</span>
          <code>POST /api/payment/initiate</code>
        </div>
        <div className="payapi-infoitem">
          <span className="payapi-label">Auth</span>
          <code>Not Required</code>
        </div>
        <div className="payapi-infoitem">
          <span className="payapi-label">Format</span>
          <code>JSON</code>
        </div>
      </div>

      {/* Main Container */}
      <div className="payapi-container">
        {/* Sidebar */}
        <aside className="payapi-sidebar">
          <nav className="payapi-nav">
            {[
              { id: 'request', icon: <FaCode />, label: 'Request' },
              { id: 'response', icon: <FaCheckCircle />, label: 'Response (201)' },
              { id: 'errors', icon: <FaTimesCircle />, label: 'Errors' },
              { id: 'fields', icon: <FaDatabase />, label: 'Details' },
              { id: 'flow', icon: <FaBook />, label: 'Flow' },
              { id: 'examples', icon: <FaCode />, label: 'Examples' },
            ].map(item => (
              <button
                key={item.id}
                className={`payapi-navitem ${selectedTab === item.id ? 'payapi-active' : ''}`}
                onClick={() => setSelectedTab(item.id)}
              >
                {item.icon}
                <span>{item.label}</span>
                <FaChevronRight />
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="payapi-content">
          {/* REQUEST */}
          {selectedTab === 'request' && (
            <section>
              <h2>Request Payload</h2>
              <table className="payapi-table">
                <thead>
                  <tr>
                    <th>Field</th><th>Type</th><th>Req</th><th>Description</th><th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(payment.request_payload).map(([key, field]) => (
                    <tr key={key}>
                      <td className="payapi-fieldname"><code>{key}</code></td>
                      <td className="payapi-fieldtype"><span className="payapi-badge">{field.type}</span></td>
                      <td className="payapi-fieldreq">{field.required ? '✓' : '-'}</td>
                      <td>{field.description}</td>
                      <td className="payapi-fieldexample"><code>{field.example}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="payapi-codeblock">
                <div className="payapi-codeheader">
                  <span>Example Request</span>
                  <button
                    className={`payapi-copybtn ${copiedCode === 'payload' ? 'payapi-copied' : ''}`}
                    onClick={() => copyToClipboard(renderJSON(payment.example_request), 'payload')}
                  >
                    {copiedCode === 'payload' ? <><FaCheck /> Copied</> : <><FaCopy /> Copy</>}
                  </button>
                </div>
                <pre>{renderJSON(payment.example_request)}</pre>
              </div>
            </section>
          )}

          {/* RESPONSE */}
          {selectedTab === 'response' && (
            <section>
              <h2>Success Response (201)</h2>
              <div className="payapi-statusbadge payapi-success">201 Created</div>

              <h3>Response Fields</h3>
              <div className="payapi-responsefields">
                {[
                  { label: 'success', type: 'boolean', value: 'true' },
                  { label: 'message', type: 'string', value: 'Payment session created successfully' },
                  { label: 'data.checkout_url', type: 'string', value: 'URL to redirect user', highlight: true },
                  { label: 'data.session_id', type: 'string', value: 'Unique session ID' },
                  { label: 'data.transaction_id', type: 'string', value: 'Unique transaction ID' },
                  { label: 'data.amount', type: 'float', value: 'Payment amount' },
                  { label: 'data.merchant_name', type: 'string', value: 'Merchant name' },
                ].map((item, idx) => (
                  <div key={idx} className={`payapi-responseitem ${item.highlight ? 'payapi-highlight' : ''}`}>
                    <span className="payapi-label">{item.label}</span>
                    <span className="payapi-type">{item.type}</span>
                    <span className="payapi-value">{item.value}</span>
                  </div>
                ))}
              </div>

              <div className="payapi-codeblock">
                <div className="payapi-codeheader">
                  <span>Complete Response</span>
                  <button
                    className={`payapi-copybtn ${copiedCode === 'success' ? 'payapi-copied' : ''}`}
                    onClick={() => copyToClipboard(renderJSON(payment.response_success.response_body), 'success')}
                  >
                    {copiedCode === 'success' ? <><FaCheck /> Copied</> : <><FaCopy /> Copy</>}
                  </button>
                </div>
                <pre>{renderJSON(payment.response_success.response_body)}</pre>
              </div>

              <div className="payapi-note payapi-info">
                <strong>Always redirect user to checkout_url after receiving response</strong>
              </div>
            </section>
          )}

          {/* ERRORS */}
          {selectedTab === 'errors' && (
            <section>
              <h2>Error Responses</h2>

              {[
                { code: '422', title: 'Validation Error', desc: 'One or more fields are invalid', dataKey: 'response_error_validation', id: 'validation' },
                { code: '404', title: 'Merchant Not Found', desc: "MID doesn't exist in the system", dataKey: 'response_error_merchant_not_found', id: 'merchant' },
                { code: '500', title: 'Server Error', desc: 'Internal server error occurred', dataKey: 'response_error_server', id: 'server' },
              ].map(error => (
                <div key={error.id} className="payapi-errorblock">
                  <div className="payapi-errorheader">
                    <span className="payapi-statuscode payapi-error">{error.code}</span>
                    <h3>{error.title}</h3>
                  </div>
                  <p>{error.desc}</p>
                  <div className="payapi-codeblock">
                    <div className="payapi-codeheader">
                      <span>Example</span>
                      <button
                        className={`payapi-copybtn ${copiedCode === error.id ? 'payapi-copied' : ''}`}
                        onClick={() => copyToClipboard(renderJSON(payment[error.dataKey].response_body), error.id)}
                      >
                        {copiedCode === error.id ? <><FaCheck /> Copied</> : <><FaCopy /> Copy</>}
                      </button>
                    </div>
                    <pre>{renderJSON(payment[error.dataKey].response_body)}</pre>
                  </div>
                </div>
              ))}

              <h3>Status Codes</h3>
              <div className="payapi-statusgrid">
                {Object.entries(docData.status_codes).map(([code, message]) => (
                  <div key={code} className={`payapi-statusitem payapi-code-${code}`}>
                    <span className="payapi-code">{code}</span>
                    <span>{message}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* FIELDS */}
          {selectedTab === 'fields' && (
            <section>
              <h2>Field Details</h2>

              <div className="payapi-detailsblock">
                <h3>💰 Commission Calculation</h3>
                <p><strong>Admin Commission:</strong> {docData.commission_details.admin_commission_percentage}%</p>
                <div className="payapi-calcexample">
                  {[
                    { label: 'Amount:', value: `₹${docData.commission_details.example.amount}` },
                    { label: 'Admin Commission:', value: '₹25.00', highlight: true },
                    { label: 'Distributor Gets:', value: '₹975.00' },
                  ].map((item, idx) => (
                    <div key={idx} className="payapi-calcitem">
                      <span>{item.label}</span>
                      <span className={item.highlight ? 'payapi-highlight' : ''}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="payapi-detailsblock">
                <h3>Validation Rules</h3>
                <div className="payapi-ruleslist">
                  {Object.entries(docData.validation_rules).map(([key, rule]) => (
                    <div key={key} className="payapi-rule">
                      <code>{key}</code>
                      <span>{rule}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="payapi-detailsblock">
                <h3>Important Notes</h3>
                <ul className="payapi-notes">
                  {docData.important_notes.map((note, idx) => (
                    <li key={idx}>
                      <span>✓</span>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* FLOW */}
          {selectedTab === 'flow' && (
            <section>
              <h2>Payment Flow</h2>
              <div className="payapi-flow">
                {Object.entries(docData.payment_flow).map(([key, step], idx) => (
                  <div key={key}>
                    <div className="payapi-flowitem">
                      <div className="payapi-flownum">{idx + 1}</div>
                      <div className="payapi-flowcontent">
                        <h4>{step.title}</h4>
                        <p>{step.action}</p>
                        {step.method && <code>{step.method.toUpperCase()}</code>}
                        {step.endpoint && <code>{step.endpoint}</code>}
                        {step.url && <code>{step.url}</code>}
                      </div>
                    </div>
                    {idx < Object.entries(docData.payment_flow).length - 1 && <div className="payapi-flowarrow">↓</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* EXAMPLES */}
          {selectedTab === 'examples' && (
            <section>
              <h2>Code Examples</h2>

              {[
                { title: 'JavaScript', id: 'js', label: 'Fetch API', code: docData.javascript_example },
                { title: 'cURL', id: 'curl', label: 'Command Line', code: docData.curl_example },
              ].map(example => (
                <div key={example.id} className="payapi-example">
                  <h3>{example.title}</h3>
                  <div className="payapi-codeblock">
                    <div className="payapi-codeheader">
                      <span>{example.label}</span>
                      <button
                        className={`payapi-copybtn ${copiedCode === example.id ? 'payapi-copied' : ''}`}
                        onClick={() => copyToClipboard(example.code, example.id)}
                      >
                        {copiedCode === example.id ? <><FaCheck /> Copied</> : <><FaCopy /> Copy</>}
                      </button>
                    </div>
                    <pre>{example.code}</pre>
                  </div>
                </div>
              ))}
            </section>
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className="payapi-footer">
        <p>A1XPAY Payment Gateway API v{docData.version} • {docData.environment}</p>
      </footer>
    </div>
  );
};

export default APIs;